import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { GoBackButton, SearchBar, SideDrawer } from '@app/components';
import { Strings } from '@app/constants';
import useResponsive from '@app/hooks/use-responsive';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { MenuMockData } from '@app/fixtures/mock-menu-data';

interface Props {
  openNav: any;
  onCloseNav: VoidFunction;
}

const SelectIngredientsScreen = ({ openNav, onCloseNav }: Props) => {
  const theme = useTheme();
  const isMobile = useResponsive('down', 'sm');

  const navigate = useNavigate();
  const { mealType, dishType, title } = openNav || {};

  const [selectedItems, setSelectedItems] = useState<{
    [key: string]: { id: number; label: string }[];
  }>({
    Proteins: [],
    Vegetables: [],
    Fruits: [],
  });

  const handleCheckBoxPress = (categoryName: string, itemId: number, itemLabel: string) => {
    const currentSelectedItems = selectedItems[categoryName];
    const newSelectedItems = [...currentSelectedItems];

    const index = newSelectedItems.findIndex((item) => item.id === itemId);
    if (index > -1) {
      newSelectedItems.splice(index, 1);
    } else {
      newSelectedItems.push({ id: itemId, label: itemLabel });
    }

    setSelectedItems({
      ...selectedItems,
      [categoryName]: newSelectedItems,
    });
  };

  const getSelectedItemsArray = () => {
    let selectedItemsArray: { id: number; label: string }[] = [];
    Object.keys(selectedItems).forEach((category) => {
      selectedItemsArray = selectedItemsArray.concat(selectedItems[category]);
    });
    return selectedItemsArray;
  };

  const handleClearAll = () => {
    setSelectedItems({
      Proteins: [],
      Vegetables: [],
      Fruits: [],
    });
  };

  const handleFindARecipe = () => {
    navigate(PATH_DASHBOARD.ingredientBasedRecipeScreen, {
      state: {
        mealType: title,
        dishType,
        selectedItems: getSelectedItemsArray(),
      },
    });
  };

  return (
    <SideDrawer openNav={openNav} onCloseNav={onCloseNav}>
      <GoBackButton onClick={onCloseNav} sx={{ width: 40, position: 'absolute' }} />

      <Typography variant="h3" textAlign="center" color={theme.palette.primary.light}>
        {title}
      </Typography>
      <Typography variant="body2" color={'#CD5A5A'}>
        {Strings.common.whatIngredients}
      </Typography>

      <SearchBar
        inputProps={{ readOnly: true }}
        onClick={() => navigate(PATH_DASHBOARD.searchScreen)}
        sx={{
          backgroundColor: theme.palette.primary.light,
          borderRadius: '50px',
          color: theme.palette.tertiary.shadyText,
          height: '40px',
        }}
        placeholder={Strings.field.searchDishes}
      />

      <List>
        {MenuMockData.slice(title === 'Juices/Shakes' ? 1 : 0).map((category, index) => (
          <Box key={index}>
            <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', mt: 2 }}>
              <Typography variant="h6" color="textDisabled">
                {category.collectionName}
              </Typography>
              {category.collectionName === 'Proteins' && (
                <Button onClick={handleClearAll} size="small" color="primary">
                  {Strings.button.clear}
                </Button>
              )}
            </Stack>
            <Stack
              sx={{
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
            >
              {category.menuItems.map((item, itemIndex) => {
                const isChecked = selectedItems[category.collectionName].some(
                  (selectedItem) => selectedItem.id === item.id
                );

                return (
                  <Box
                    key={itemIndex}
                    sx={{
                      flexBasis: isMobile ? '50%' : '33%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() =>
                            handleCheckBoxPress(category.collectionName, item.id, item.label)
                          }
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2"> {item.label}</Typography>}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}
      </List>

      <Button
        variant="contained"
        sx={{
          backgroundColor: theme.palette.primary.light,
        }}
        fullWidth
        disabled={getSelectedItemsArray().length === 0}
        onClick={handleFindARecipe}
      >
        {Strings.button.findRecipe}
      </Button>
    </SideDrawer>
  );
};

export { SelectIngredientsScreen };
